import React from 'react';

import CustomIcon from 'Crow/Form/Field/Custom/Common/CustomIcon/CustomIcon.js';

import {
	getSession,
	logout,
} from 'Vulture/Session.js';

import {
	hasGlobalScope,
	hasPermission,
	isNarrowScoped,
} from 'Vulture/Permissions';

import {
	dlAccountSelectPush,
	dlLogoutPush,
	dlNavigationPush,
	dlToolsMenu,
	dlUserMenu,
} from 'Vulture/DataLayer.js';

import { sparkieEnabled } from 'Vulture/lib/miaw/flags.js';
import style from './Header.module.css';

import hierarchyDropdownStyle from './SystemsHierarchyDropdown.module.css';

import {
	cardinalHost,
} from 'Vulture/ENV';

import SystemsTable from './SystemsTable';
import Img from 'wrapper/Img';

class HeaderView extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			accountDropdownClass:   style.accountDropdownHidden,
			hierarchyDropdownClass: style.hierarchyDropdownHidden,
			userDropdownClass:      style.userDropdownHidden,
			toolsDropdownClass:     style.toolsHidden,
			encryptedKey:           null,
		};
	}

	async logout() {
		const {
			app,
		} = this.props;

		try {
			await logout();
		} catch (err) {
			console.error(err);
		}

		app.logout();
	}

	shouldComponentUpdate(nextProps, nextState) {
		if (this.state !== nextState) return true;
		if (this.props.accountUUID !== nextProps.accountUUID) return true;
		if (this.props.mode !== nextProps.mode) return true;
		if (this.props.accountName !== nextProps.accountName) return true;
		if (this.props.systemGroupUUID !== nextProps.systemGroupUUID) return true;
		return false;
	}

	componentDidMount() {
		// The Header can mount with the account already resolved (deep-link/refresh
		// inside an account, or the full reload leaving system_programming: boot runs
		// the accounts and permissions fetches concurrently and only renders the
		// Header once permissions land, so setAccountUUID may already have fired).
		// In that case componentDidUpdate never sees an accountUUID change, and the
		// Cardinal link would render with s=null forever — encrypt on mount too.
		this.encryptCardinalKey();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.accountUUID === this.props.accountUUID) {
			return;
		}

		this.encryptCardinalKey();
	}

	// Encrypts the session key with the selected account's UUID for the Cardinal
	// (Wayfinding) link's `s=` param. No-op without an account.
	encryptCardinalKey() {
		const {
			accountUUID,
		} = this.props;

		if (!accountUUID) {
			this.setState({
				encryptedKey: null,
			});
			return;
		}

		const {
			session = '',
		} = getSession();

		let key = accountUUID.substring(0, 32);

		const env = window._getEnv('COMMITHASH');

		let iv = env;
		while (iv.length < 12) {
			iv += env;
		}
		iv = iv.substring(0, 12);

		const encoder = new TextEncoder();
		const keyBuffer = encoder.encode(key);
		const ivBuffer = encoder.encode(iv);
		const tokenBuffer = encoder.encode(session);

		// Import the key
		crypto.subtle.importKey('raw', keyBuffer, {
			name: 'AES-GCM',
		}, false, [ 'encrypt', 'decrypt', ])
			.then((cryptoKey) => {
				return crypto.subtle.encrypt({
					name: 'AES-GCM',
					iv:   ivBuffer,
				}, cryptoKey, tokenBuffer);
			})
			.then((encryptedKey) => {
				let bin = '';
				const bytes = new Uint8Array(encryptedKey);
				for (const byte of bytes) {
					bin += String.fromCharCode(byte);
				}

				this.setState({
					encryptedKey: window.btoa(bin),
				});
			})
			.catch((err) => {
				console.error(err);
			});
	}

	renderHierarchyDropdown() {
		const {
			app,
			accountUUID,
			accountList,
			fd,
			systemGroupUUID,
			mode,
		} = this.props;

		let shouldShowSystemGroups = false;

		switch (mode) {
		case 'content':
		case 'systems':
			shouldShowSystemGroups = true;
			break;
		default:
			break;
		}

		const buildSystemGroups = (m1Key) => {
			return Object
				.keys(fd[m1Key] || {})
				.filter((m2Key) => {
					if (!m2Key.startsWith('system_group.')) {
						return false;
					}

					// Users narrowed below account level (property- or slideshow-scoped) don't see
					// system groups that contain no visible systems.
					if (isNarrowScoped()) {
						let hideGroup = true;
						const m2Data = fd[m2Key];

						for (const [ systemKey, ] of Object.entries(m2Data || {})) {
							if (systemKey.startsWith('system.') || systemKey.startsWith('system_group.')) {
								if (systemKey.startsWith('system.') && fd[systemKey]) {
									// Check if system is hidden
									if (!fd[systemKey]['hidden']) {
										hideGroup = false;
										break;
									}
								}
								if (systemKey.startsWith('system_group.') && fd[systemKey]) {
									// Recursively check sub-groups for visible systems
									for (const [ subGroupSystemkey, ] of Object.entries(fd[systemKey] || {})) {
										if (subGroupSystemkey.startsWith('system.') && fd[subGroupSystemkey]) {
											if (!fd[subGroupSystemkey]['hidden']) {
												hideGroup = false;
												break;
											}
										}
									}
									if (!hideGroup) break;
								}
							}
						}

						return !hideGroup;
					}

					return true;
				})
				.sort((a, b) => {
					return fd[a]?.display_name > fd[b]?.display_name ? 1 : -1;
				})
				.map((m2Key) => {
					const isSelected = m2Key === 'system_group.' + systemGroupUUID;
					const projectNumber = fd[m2Key]['project_number'];
					const name = (
						<div
							className={style.oneLine}
						>
							<div className={`${style.displayName} ${isSelected ? hierarchyDropdownStyle.selectedSystemGroupDisplayName : ''}`}>{fd[m2Key].display_name}</div>
						</div>
					);

					return {
						id:           m2Key.split('.')[1],
						name,
						m1:           'system_group',
						projectNumber,
						status:       '',
						subRows:      buildSystemGroups(m2Key),
						components:   [],
						path:         '',
						searchValues: {
							name:            fd[m2Key].display_name,
							projectNumber:   '',
							resolution:      '',
							status:          '',
							macAddress:      '',
							orientation:     '',
							directoryNumber: '',
						},
					};
				});
		};

		const systemGroups = buildSystemGroups(`account.${accountUUID}`);

		return (
			<div className={`${style.hierarchyMenu} ${this.state.hierarchyDropdownClass} ${accountList.length === 0 && style.buttonNoAccounts}`} data-test-id="hierarchyMenu">
				<button className={style.button}
					onClick={(e) => {
						dlNavigationPush({
							destination: 'All Accounts',
						});

						this.setState({
							hierarchyDropdownClass: style.hierarchyDropdownHidden,
						}, () => {
							app.redirect({
								to: `/accounts`,
							});
						});
					}}
				>All Accounts</button>
				{accountList
					.sort((a, b) => {
						const aM1UUID = Object.keys(a)[0];
						const aM1Name = Object.values(a)[0];
						const bM1UUID = Object.keys(b)[0];
						const bM1Name = Object.values(b)[0];

						if (aM1UUID === accountUUID) {
							return -1;
						}

						if (bM1UUID === accountUUID) {
							return 1;
						}

						return aM1Name > bM1Name ? 1 : -1;
					})
					.map((accountObj, i) => {
						const m1UUID = Object.keys(accountObj)[0];
						const m1Name = Object.values(accountObj)[0];

						return (
							<React.Fragment key={i}>
								<button className={style.button}
									onClick={(e) => {
										e.preventDefault();

										dlAccountSelectPush({
											accountName: m1Name,
										});

										this.setState({
											hierarchyDropdownClass: style.hierarchyDropdownHidden,
										}, () => {
											// Cross-account: keep only step, drop entity/system (account-scoped).
										const stepMatch = mode === 'systems' ? window.location.hash.match(/step=\d/) : null;
										const preservedHash = stepMatch ? `#${stepMatch[0]}` : '';
											app.setAccountUUID(m1UUID);
											if (systemGroupUUID || mode === 'library') {
												// Redirect to the NEWLY selected account (m1UUID), not the
												// one we're switching away from.
												app.redirect({
													// Redirect to the NEWLY selected account (m1UUID, our
													// stale-account fix) AND preserve the step hash (main).
													to: `/systems/${m1UUID}${preservedHash}`,
												});
											}
											if (preservedHash) {
												setTimeout(() => {
													window.history.replaceState(null, '', window.location.pathname + window.location.search + preservedHash);
												}, 0);
											}
										});
									}}
								>{m1Name}</button>
								{shouldShowSystemGroups && m1UUID === accountUUID && <SystemsTable
									style={hierarchyDropdownStyle}
									tbodyTestId='systemsHierarchyTableDropdown'
									array={systemGroups}
									systemGroupUUID={systemGroupUUID}
									defaultExpanded={systemGroups.map(() => {
										return {
											index: true,
										};
									})}
									systemsTableRowOnclick={(_systemGroupUUID) => {
										this.setState({
											hierarchyDropdownClass: style.hierarchyDropdownHidden,
										}, () => {
											// Cross-account: keep only step, drop entity/system (account-scoped).
										const stepMatch = mode === 'systems' ? window.location.hash.match(/step=\d/) : null;
										const preservedHash = stepMatch ? `#${stepMatch[0]}` : '';
											app.setSystemGroupUUID(systemGroupUUID === _systemGroupUUID ? '' : _systemGroupUUID);
											app.redirect({
												to: `/systems/${accountUUID}${preservedHash}`,
											});
											if (preservedHash) {
												setTimeout(() => {
													window.history.replaceState(null, '', window.location.pathname + window.location.search + preservedHash);
												}, 0);
											}
										});
									}}
								/>}
							</React.Fragment>
						);
					})}
			</div>
		);
	}

	renderUserDropdown() {
		const {
			app,
			fd,
			accountUUID,
		} = this.props;

		return (
			<div className={`${style.userMenu} ${this.state.userDropdownClass}`} data-test-id="userDropdown">
				<button
					data-test-id="myProfileButton"
					className={style.button}
					onClick={(e) => {
						e.preventDefault();
						dlNavigationPush({ destination: 'My Profile' });
						app.redirect({ to: '/user_profile' });
						this.setState({ userDropdownClass: style.userDropdownHidden });
					}}
				>My Profile</button>
				{fd?.[`account.${accountUUID}`]?.show_billing && <button
					data-test-id="billingButton"
					className={style.button}
					onClick={async (e) => {
						e.preventDefault();

						dlNavigationPush({
							destination: 'Billing',
						});

						let to = `/billing/${accountUUID}`;

						app.redirect({
							to,
						});

						this.setState({
							toolsDropdownClass: style.toolsHidden,
						});
					}}
				>Billing</button>}
				<button
					data-test-id="logoutButton"
					className={style.button}
					onClick={async (e) => {
						dlLogoutPush();
						await this.logout();
					}}
				>Logout</button>
			</div>
		);
	}

	renderSupport() {
		return '';

		// TODO: fix SF Web2Case form

		// const {
		// 	app,
		// 	accountUUID,
		// } = this.props;

		// return (
		// 	<a href='' onClick={(e) => {
		// 		e.preventDefault();

		// 		let to = '/support';
		// 		if (accountUUID) {
		// 			to = `/support/${accountUUID}`;
		// 		}

		// 		app.redirect({
		// 			to,
		// 		});
		// 	}}
		// 	className={`${style.link} ${style.userSupport}`}
		// 	>
		// 		<CustomIcon icon='headset' />&nbsp;
		// 		<div className={`${style.pullDownName} ${style.pullDownAccountName}`}>Need Assistance?</div>
		// 	</a>
		// );
	}

	renderSparkieLauncher() {
		const {
			app,
		} = this.props;

		return (
			<div
				className={style.sparkieLauncher}
				onClick={(e) => {
					e.preventDefault();
					app.toggleSparkie();
				}}
				data-onclick="Sparkie Assistant"
				data-test-id="sparkieLauncher"
				title="Ask Sparkie"
			>
				<CustomIcon icon='comment-dots' />
			</div>
		);
	}

	// Visible when any Tools item would render: Admin Users / system assignment
	// (global scope), the tools_access tools, or the Cardinal link.
	canSeeToolsMenu() {
		return hasGlobalScope() || hasPermission('tools_access') || hasPermission('cardinal_view');
	}

	renderToolsDropdown() {
		const {
			app,
			accountUUID,
		} = this.props;

		const {
			encryptedKey,
		} = this.state;

		let themeMenu = (
			<li data-test-id="toolsMenuItem--ThemeEditor">
				<a href='' onClick={(e) => {
					e.preventDefault();

					dlNavigationPush({
						destination: 'Theme Editor',
					});

					let to = '/theme_editor';
					if (accountUUID) {
						to = `/theme_editor/${accountUUID}`;
					}

					app.redirect({
						to,
					});

					this.setState({
						toolsDropdownClass: style.toolsHidden,
					});
				}}><CustomIcon icon="swatchbook"/>Theme Editor</a>
			</li>
		);

		let screenshotView = (
			<li data-test-id="toolsMenuItem--Screenshots">
				<a href='' onClick={(e) => {
					e.preventDefault();

					dlNavigationPush({
						destination: 'Screenshot',
					});

					let to = '/screenshots';
					if (accountUUID) {
						to = `/screenshots/${accountUUID}`;
					}

					app.redirect({
						to,
					});

					this.setState({
						toolsDropdownClass: style.toolsHidden,
					});
				}}><CustomIcon icon="book"/>Screenshots</a>
			</li>
		);

		const systemAssignment = (
			<li data-test-id="toolsMenuItem--SystemGroupReassignment">
				<a href='' onClick={(e) => {
					e.preventDefault();

					dlNavigationPush({
						destination: 'System Assignment',
					});

					let to = '/system_assignment';

					app.redirect({
						to,
					});

					this.setState({
						toolsDropdownClass: style.toolsHidden,
					});
				}}><CustomIcon icon="book"/>System Group Reassignment</a>
			</li>
		);

		const entertainment = (
			<li data-test-id="toolsMenuItem--Entertainment">
				<a href='' onClick={(e) => {
					e.preventDefault();

					dlNavigationPush({
						destination: 'Entertainment',
					});

					let to = '/entertainment';
					if (accountUUID) {
						to = `/entertainment/${accountUUID}`;
					}

					app.redirect({
						to,
					});

					this.setState({
						toolsDropdownClass: style.toolsHidden,
					});
				}}><CustomIcon icon="book"/>Entertainment</a>
			</li>
		);

		const dataPresetsView = (
			<li data-test-id="toolsMenuItem--DataPresets">
				<a href='' onClick={(e) => {
					e.preventDefault();

					dlNavigationPush({
						destination: 'DataPresets',
					});

					let to = '/data_presets';
					if (accountUUID) {
						to = `/data_presets/${accountUUID}`;
					}

					app.redirect({
						to,
					});

					this.setState({
						toolsDropdownClass: style.toolsHidden,
					});
				}}><CustomIcon icon="laptop-code"/>Data Presets</a>
			</li>
		);

		const cardinalLink = (
			<li data-test-id="toolsMenuItem--Wayfinding">
				<a
					href={`${cardinalHost}/?cardinal_account=${accountUUID}&s=${encodeURIComponent(encryptedKey)}`}
					target='_blank'
					rel='noreferrer'
					onClick={() => {
						dlNavigationPush({
							destination: 'Cardinal',
						});
					}}
				>
					<CustomIcon icon="fa-location-arrow"/>Wayfinding
				</a>
			</li>
		);

		const migrateProperty = (
			<li data-test-id="toolsMenuItem--MigrateProperty">
				<a href='' onClick={(e) => {
					e.preventDefault();

					dlNavigationPush({
						destination: 'Migrate Property',
					});

					let to = '/migration';
					if (accountUUID) {
						to = `/migration/${accountUUID}`;
					}

					app.redirect({
						to,
					});

					this.setState({
						toolsDropdownClass: style.toolsHidden,
					});
				}}><CustomIcon icon="exchange-alt"/>Migrate Property</a>
			</li>
		);

		if (this.canSeeToolsMenu()) {
			return (
				<div className={`${style.toolsMenu} ${this.state.toolsDropdownClass}`} data-test-id="toolsMenu">
					<ul className={style.toolsList}>
						{hasGlobalScope() ? <li data-test-id="toolsMenuItem--AdminUsers">
							<a href='' onClick={(e) => {
								e.preventDefault();

								dlNavigationPush({
									destination: 'Admin Users',
								});

								// Unified user management: the admin entry opens the full
								// (unfiltered) user list; the sidebar "Users" entry keeps
								// the account context.
								app.redirect({
									to: '/userAdmin',
								});

								this.setState({
									toolsDropdownClass: style.toolsHidden,
								});
							}}><CustomIcon icon="users"/>Admin Users</a>
						</li> : ''}
						{hasPermission('tools_access') ? themeMenu : ''}
						{hasPermission('tools_access') ? dataPresetsView : ''}
						{hasPermission('tools_access') ? screenshotView : ''}
						{accountUUID && encryptedKey && hasPermission('cardinal_view') ? cardinalLink : ''}
						{hasGlobalScope() && hasPermission('system_edit') ? systemAssignment : ''}
						{hasPermission('tools_access') ? entertainment : ''}
						{hasPermission('tools_access') ? migrateProperty : ''}
					</ul>
				</div>
			);
		}
	}

	render() {
		const {
			accountList,
			accountName,
			fd,
			systemGroupUUID,
			mode,
		} = this.props;

		const {
			email = '',
		} = getSession();

		const selectedAccountName = accountName || 'Search Accounts';

		let dropdownText = selectedAccountName;

		switch (mode) {
		case 'content':
		case 'systems':
			dropdownText = fd['system_group.' + systemGroupUUID]?.display_name || selectedAccountName;
			break;
		default:
			break;
		}

		let toolsDropdown = null;
		if (this.canSeeToolsMenu()) {
			toolsDropdown = (
				<div
					className={`${style.link} ${this.state.toolsDropdownClass}`}
					onClick={(e) => {
						e.preventDefault();

						dlToolsMenu();

						if (this.state.toolsDropdownClass === style.toolsHidden) {
							this.setState({
								toolsDropdownClass: style.toolsVisible,
							});
							return;
						}
						this.setState({
							toolsDropdownClass: style.toolsHidden,
						});
					}}
					data-onclick="Tools Navigation"
					data-test-id="toolsMenuButton"
				>
					<div className={`${style.pullDownName} ${style.toolsTextBox}`}>TOOLS</div>
				</div>
			);
		}

		return (
			<React.Fragment>
				<div className={`${style.popoverBackground} ${this.state.hierarchyDropdownClass} ${this.state.userDropdownClass} ${this.state.toolsDropdownClass}`}
					onClick={(e) => {
						e.preventDefault();
						this.setState({
							hierarchyDropdownClass: style.hierarchyDropdownHidden,
							userDropdownClass:      style.userDropdownHidden,
							toolsDropdownClass:     style.toolsHidden,
						});
					}}/>
				<nav
					className={`${style.header}`}
				>
					<div className={style.hierarchy}>
						<div className={style.logo} data-onclick="The Touchsource Logo in header" data-test-id="headerLogo">
							<a href='https://touchsource.com/' target='_blank' rel="noopener noreferrer">
								<Img src='https://ts-public-images.s3.amazonaws.com/TouchSourceSPARK_logo_FINAL_TS_whiteblue.png' alt="The Touchsource Spark PX logo" />
							</a>
						</div>
						<div className={style.hierarchyDropdown}>
							<div className={style.hierarchyDropdown}>
								<div className={style.vertLine}></div>
								<div
									data-test-id="hierarchyDropdownVisible"
									className={`${style.link} ${this.state.hierarchyDropdownClass}`}
									onClick={(e) => {
										e.preventDefault();
										if (this.state.hierarchyDropdownClass === style.hierarchyDropdownHidden) {
											this.setState({
												hierarchyDropdownClass: style.hierarchyDropdownVisible,
											});

											return;
										}

										this.setState({
											hierarchyDropdownClass: style.hierarchyDropdownHidden,
										});
									}}
									data-onclick="Project Navigation"
								>
									<div className={style.pullDownName}>{dropdownText}</div>
									<CustomIcon icon='caret-down'/>
								</div>
							</div>
						</div>
					</div>
					<div className={style.userAccountContainer}>
						<div className={style.user}>
							<div className={style.userDropdown}>
								{sparkieEnabled() ? this.renderSparkieLauncher() : null}
								{this.renderSupport()}
								{toolsDropdown}
								<div
									className={`${style.link} ${this.state.userDropdownClass}`}
									onClick={(e) => {
										e.preventDefault();
										dlUserMenu();
										if (this.state.userDropdownClass === style.userDropdownHidden) {
											this.setState({
												userDropdownClass: style.userDropwdownVisible,
											});

											return;
										}
										this.setState({
											userDropdownClass: style.userDropdownHidden,
										});
									}}
									data-onclick="User Navigation"
									data-test-id="userNavigation"
								>
									<CustomIcon icon='user'/>&nbsp;
									<div className={`${style.pullDownName} ${style.pullDownAccountName}`}>{email}</div> <CustomIcon className='caret' icon='caret-down'/>
								</div>
							</div>
						</div>
					</div>
				</nav>
				{this.renderHierarchyDropdown()}
				{this.renderUserDropdown()}
				{this.canSeeToolsMenu() ? this.renderToolsDropdown() : null}
			</React.Fragment>
		);
	}
}

export default HeaderView;